import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CButton, CCard, CCardBody } from "@coreui/react-pro";
import {
  type PartnerOfferDecision,
  type PartnerOfferDto,
} from "../../api/hooks/usePartnerOffers";

interface PartnerOfferCardProps {
  offer: PartnerOfferDto;
  onDecision: (decision: PartnerOfferDecision) => void;
  /** Disables every control while a decision is in flight, so a double-click can't double-count. */
  busy?: boolean;
  /** The dashboard slot shows the dismissals; the Partner offers page manages state its own way. */
  showDismissals?: boolean;
  /** Extra classes on the card. The offers page passes h-100 so a row of them lines up. */
  className?: string;
  /** Rendered above the copy. The offers page puts the customer's standing and its undo here. */
  statusSlot?: ReactNode;
}

/**
 * One partner offer, in whichever of its two states the API says it's in: the offer itself, or the
 * follow-up asking whether they got set up after clicking through.
 *
 * The card renders from the API's `visibility` rather than deciding for itself — the snooze windows
 * and the cap live server-side, and duplicating them here is how the two drift apart.
 */
export function PartnerOfferCard({
  offer,
  onDecision,
  busy = false,
  showDismissals = true,
  className = "mb-4",
  statusSlot,
}: PartnerOfferCardProps) {
  const { t } = useTranslation("partnerOffers");

  // Copy is per partner and keyed by the key the API sends. A partner with no copy yet still
  // renders — falling back to its key — rather than showing raw i18n paths to a customer.
  const key = offer.partnerKey.toLowerCase();
  const name = t(`partners.${key}.name`, { defaultValue: offer.partnerKey });
  const copy = (field: string, fallback = "") =>
    t(`partners.${key}.${field}`, { defaultValue: fallback });

  const openPartner = () => {
    // Recorded before the tab opens, not after: the click is the only part of this funnel we own,
    // and a popup blocker or a closed tab must not lose it.
    onDecision("ClickThrough");
    window.open(offer.referralLink, "_blank", "noopener,noreferrer");
  };

  if (offer.visibility === "ShowSignUpFollowUp") {
    return (
      <CCard className={className}>
        <CCardBody className="d-flex flex-column">
          {statusSlot}
          <PartnerLabel partnerKey={key} name={name} eyebrow={t("eyebrow")} />
          <h5 className="mb-1">{t("followUp.title")}</h5>
          <p className="text-body-secondary mb-3">
            {t("followUp.body", { partner: name })}
          </p>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-auto">
            <CButton
              color="primary"
              size="sm"
              disabled={busy}
              onClick={() => onDecision("ConfirmSignedUp")}
            >
              {t("followUp.confirm")}
            </CButton>
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onDecision("Snooze")}
            >
              {t("followUp.notYet")}
            </CButton>
            <CButton
              color="link"
              size="sm"
              className="px-1"
              disabled={busy}
              onClick={openPartner}
            >
              {t("followUp.openAgain", { partner: name })} ↗
            </CButton>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className={className}>
      <CCardBody className="d-flex flex-column">
        {statusSlot}
        <PartnerLabel partnerKey={key} name={name} eyebrow={t("eyebrow")} />
        <h5 className="mb-1">{copy("title", name)}</h5>
        {/* Capped so the line does not run the full width of a dashboard card — around 150
            characters, which is past the point anyone reads comfortably. 75ch is the upper end of a
            readable measure: tight enough to help, wide enough not to break mid-phrase and leave
            half the card empty, which 60ch did. */}
        <p className="text-body-secondary mb-2" style={{ maxWidth: "75ch" }}>
          {copy("body")}
        </p>

        {/* Rendered only when configured — the terms aren't confirmed for every partner, and a
            wrong discount claim is worse than no claim. */}
        {offer.offerText && (
          <p className="text-success fw-semibold mb-2">{offer.offerText}</p>
        )}

        {/* The coupon is what actually discounts them; the link only attributes them to us. */}
        {offer.couponCode && (
          <p className="mb-2 small">
            {t("couponLabel")}{" "}
            <code className="fw-semibold">{offer.couponCode}</code>
          </p>
        )}

        <div className="d-flex flex-wrap align-items-center gap-3 mt-auto pt-3">
          <CButton
            color="primary"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={openPartner}
          >
            {copy("cta", `Visit ${name}`)} ↗
          </CButton>
          {/* Rendered only when configuration supplies somewhere to go. Blank is the state to
              leave a partner in until there is an article worth sending anyone to — a link into a
              placeholder is worse than no link. */}
          {offer.learnMoreUrl && (
            <LearnMoreLink
              href={offer.learnMoreUrl}
              label={copy("learnMore", "How this works")}
            />
          )}
        </div>
      </CCardBody>

      {showDismissals && (
        <div className="border-top px-3 py-2 d-flex flex-wrap align-items-center gap-3">
          {/* Both dismissals visible rather than hidden behind a close icon. "I already use this"
              is the answer we most want — burying it is how we never collect it. */}
          <CButton
            color="link"
            size="sm"
            className="px-0 text-body-secondary text-decoration-underline"
            disabled={busy}
            onClick={() => onDecision("AlreadyUsing")}
          >
            {t("actions.alreadyUsing", { partner: name })}
          </CButton>
          <CButton
            color="link"
            size="sm"
            className="px-0 text-body-secondary text-decoration-underline"
            disabled={busy}
            onClick={() => onDecision("Snooze")}
          >
            {t("actions.notNow")}
          </CButton>
          <span className="text-body-secondary small ms-auto">
            {t("disclosure")}
          </span>
        </div>
      )}
    </CCard>
  );
}

/**
 * A help article inside the portal is a route, not a destination — an anchor would reload the whole
 * app to reach a page React Router already has. Anything absolute is somebody else's site and opens
 * in a new tab, so the customer does not lose their place.
 */
function LearnMoreLink({ href, label }: { href: string; label: string }) {
  const isInternal = href.startsWith("/");

  return isInternal ? (
    <Link to={href} className="small">
      {label}
    </Link>
  ) : (
    <a href={href} className="small" target="_blank" rel="noopener noreferrer">
      {label} ↗
    </a>
  );
}

/**
 * Marks per partner, reusing the assets and the tile treatment the brochure already ships — same
 * files, same tinted rounded tile, so a customer moving from the marketing site to the portal sees
 * the same thing. Sized individually because the source artwork has different padding baked in.
 */
const PARTNER_LOGOS: Record<string, { src: string; size: number }> = {
  traderspost: { src: "/brand/traderspost.png", size: 31 },
};

/**
 * Whose offer this is, before what it does.
 *
 * Without it a card reads as a generic "partner offer" whose subject you only work out from the
 * body copy. The mark and the name lead; the eyebrow is the category behind them.
 */
function PartnerLabel({
  partnerKey,
  name,
  eyebrow,
}: {
  partnerKey: string;
  name: string;
  eyebrow: string;
}) {
  // Two ways there can be no mark, collapsing to the same outcome: a partner we have no artwork for
  // at all, and one whose file has gone missing since. The second would otherwise leave an empty
  // tinted tile sitting there — not a crash, but an obvious blemish nobody would notice in review.
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = logoFailed ? undefined : PARTNER_LOGOS[partnerKey];

  return (
    <div className="mb-3 d-flex align-items-center gap-2">
      {/* No tile at all when there is no mark — the name still carries it, so a third partner added
          in configuration renders fine before anyone sources a logo. */}
      {logo && (
        <span
          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "var(--sl-accent-soft)",
            overflow: "hidden",
          }}
        >
          <img
            src={logo.src}
            alt=""
            aria-hidden="true"
            onError={() => setLogoFailed(true)}
            style={{ width: logo.size, height: logo.size, objectFit: "contain" }}
          />
        </span>
      )}
      <span className="d-flex flex-column lh-sm">
        <span className="fw-bold">{name}</span>
        <span
          className="text-body-secondary text-uppercase"
          style={{ fontSize: ".68rem", letterSpacing: ".08em" }}
        >
          {eyebrow}
        </span>
      </span>
    </div>
  );
}

export default PartnerOfferCard;
